import React, { useMemo } from 'react';

import Button from '@atlaskit/button/custom-theme-button';
import SectionMessage from '@atlaskit/section-message';

import {
  ALLOWED_PLAN_URL,
  DOCS_CUSTOM_PIPELINE_URL,
  YML_FILE_NAME,
} from '../const';

export type Props = {
  isPipelinesDisabled?: boolean;
  isRepoReadOnly?: boolean;
  isOverAllowance?: boolean;
  isMissingPipelineDefinition?: boolean;
  error?: any;
  plansPageUrl: string;
  settingsPageUrl: string;
};

/* eslint @typescript-eslint/ban-types: "warn" */
const RunPipelineMessage: React.FC<Props> = ({
  isPipelinesDisabled,
  isRepoReadOnly,
  isOverAllowance,
  isMissingPipelineDefinition,
  error,
  plansPageUrl,
  settingsPageUrl,
}) => {
  const messageData = useMemo(
    () => [
      {
        key: '',
        text: ({ errorMessage }: any) => errorMessage,
      },
      {
        key: 'plan-service.parse.missing-section',
        text: ({ parentElement, element }: any) => (
          <>
            The {parentElement} section in your <strong>{YML_FILE_NAME}</strong>{' '}
            file is missing a {element}. Add the missing {element} to get this
            working.
          </>
        ),
      },
      {
        key: 'plan-service.parse.missing-string-specific',
        text: ({ parentElement, element }: any) => (
          <>
            The {element} in the {parentElement} section of your{' '}
            <strong>{YML_FILE_NAME}</strong> file is missing a string. Add the
            missing string to to get this working.
          </>
        ),
      },
      {
        key: 'plan-service.parse.invalid-list',
        text: ({ element }: any) => (
          <>
            The {element} section in your <strong>{YML_FILE_NAME}</strong> file
            must be a list.
            <br />
            Check out our documentation on how to format the {element} section.
          </>
        ),
      },
      {
        key: 'plan-service.parse.invalid-map',
        text: ({ element }: any) => (
          <>
            The {element} section in your <strong>{YML_FILE_NAME}</strong> file
            must be a map.
            <br />
            Check out our documentation on how to format the {element} section.
          </>
        ),
      },
      {
        key: 'plan-service.parse.invalid-image-name',
        text: ({ element }: any) => (
          <>
            We don&apos;t recognise the image name {element} in your{' '}
            <strong>{YML_FILE_NAME}</strong> file. Correct the name to get this
            working.
          </>
        ),
      },
      {
        key: 'plan-service.parse.parse-error',
        text: () => (
          <>
            Something is misconfigured in your <strong>{YML_FILE_NAME}</strong>{' '}
            file. View it in our editor to find out where.
          </>
        ),
      },
      {
        key: 'plan-service.parse.parse-indentation-error',
        text: () => (
          <>
            The indentation in the <strong>{YML_FILE_NAME}</strong> file is
            incorrect. Correct the spacing and remove any tabs.
          </>
        ),
      },
      {
        key: 'agent.build.time-limit-exceeded',
        text: () => (
          <>
            The build stopped because one or more steps reached the timeout
            limit of two hours.
          </>
        ),
      },
      {
        key: 'agent-service.account-concurrency.reached',
        text: () => (
          <>
            You have exceeded the number of parallel steps{' '}
            <a href={ALLOWED_PLAN_URL} target="_blank">
              allowed for your plan
            </a>
            . Please review and reduce the number of parallel steps.
          </>
        ),
      },
      {
        key: 'pipeline-error',
        text: () => <>Unable to retrieve Pipeline.</>,
      },
    ],
    []
  );

  const warningMessage = useMemo(
    () =>
      error
        ? messageData.filter(l => l.key === error.key)[0] || messageData[0]
        : messageData[0],
    [error, messageData]
  );

  if (isPipelinesDisabled) {
    return (
      <SectionMessage testId="run-pipeline-message" appearance="warning">
        <p>
          Pipelines is currently <strong>disabled</strong> for this repository.
          Repository administrators can enable Pipelines in repository{' '}
          <Button
            href={settingsPageUrl}
            appearance="link"
            target="_top"
            spacing="none"
          >
            Settings → Pipelines
          </Button>
          .
        </p>
      </SectionMessage>
    );
  }

  if (isRepoReadOnly) {
    return (
      <SectionMessage testId="run-pipeline-message" appearance="warning">
        <p>
          To run custom pipelines you need <em>write</em> permission that can be
          granted by the repository administrator.
        </p>
      </SectionMessage>
    );
  }

  if (error) {
    return (
      <SectionMessage testId="run-pipeline-message" appearance="warning">
        <p>
          {warningMessage.text({
            errorMessage: error.message,
            element: error.arguments ? error.arguments.element : '',
            parentElement: error.arguments
              ? error.arguments.parent_element
              : '',
          })}{' '}
          <Button
            href={DOCS_CUSTOM_PIPELINE_URL}
            spacing="none"
            appearance="link"
            target="_blank"
          >
            Find out more
          </Button>
        </p>
      </SectionMessage>
    );
  }

  if (isOverAllowance) {
    return (
      <SectionMessage testId="run-pipeline-message" appearance="warning">
        <p>
          Your account has run out of build minutes. Upgrade your current plan
          or purchase more build minutes to continue running pipeline steps on
          the cloud. Pipeline steps on runners are unaffected.{' '}
          <Button
            href={plansPageUrl}
            appearance="link"
            target="_top"
            spacing="none"
          >
            View plan details
          </Button>
        </p>
      </SectionMessage>
    );
  }

  if (isMissingPipelineDefinition) {
    return (
      <SectionMessage testId="run-pipeline-message" title="No Pipeline">
        <p>
          You can add a pipeline in your {YML_FILE_NAME} file to manually
          trigger it from this dialog.{' '}
          <Button
            href={DOCS_CUSTOM_PIPELINE_URL}
            spacing="none"
            appearance="link"
            target="_blank"
          >
            Find out more
          </Button>
        </p>
      </SectionMessage>
    );
  }

  return (
    <SectionMessage testId="run-pipeline-message" appearance="warning">
      <p>An unidentified error occurred. Please try again.</p>
    </SectionMessage>
  );
};

export default RunPipelineMessage;
